import React from "react";
import { CheckSquare } from "@phosphor-icons/react";

const STEP_LIST = [
  ["Welcome", "what to expect (you're here)"],
  ["Business basics", "a little about you and your business"],
  ["Opening cash", "your total cash at the start of this month"],
  ["Regular costs", "all expenses that repeat regularly, including tax bills"],
  ["One-off costs", "things you'll pay for just once within the next 12 months"],
  ["Paying yourself", "what you'd like to take home each month"],
  ["Income streams", "what you sell, and anything else that brings money in"],
  ["Your rhythm", "your busier and quieter months"],
  ["Your 12-month plans", "goals, launches and big decisions"],
  ["Nearly there", "one last look, then we build your forecast"],
];

const Section = ({ title, children, testid }) => (
  <section className="space-y-3" data-testid={testid}>
    <h2 className="font-heading uppercase tracking-wide text-base md:text-lg">{title}</h2>
    {children}
  </section>
);

export const WelcomeStep = () => (
  <div className="space-y-10" data-testid="welcome-step">
    <div className="card space-y-3 bg-[#FFF7E5]">
      <p className="text-lg font-semibold">Welcome to forecastly. Lovely to have you here.</p>
      <p className="leading-relaxed text-black/80">
        Over the next few pages, we'll build your 12-month cashflow forecast together. You're already past the hardest part: deciding to look at your numbers. Woohoo!
      </p>
    </div>

    <Section title="How it works" testid="welcome-how">
      <ul className="list-disc pl-5 space-y-2 text-black/80 leading-relaxed">
        <li><strong>First, you build your forecast</strong> over the next few screens: your best guess at the next 12 months, i.e. what's coming in, what's going out, and what's left for you.</li>
        <li><strong>Then, each week you check in, and each month you log what really happened</strong> (your actuals), so your forecast gets sharper as you go.</li>
        <li><strong>Your personalised AI coach</strong> translates your numbers and what they mean into plain English, so you can be proactive and always know what's coming on the horizon.</li>
        <li><strong>Your goals</strong> each get their own tickable action plan, turning intentions into progress, one tick at a time.</li>
      </ul>
    </Section>

    <Section title="Your next 10 steps" testid="welcome-steps">
      <ol className="grid sm:grid-cols-2 gap-2">
        {STEP_LIST.map(([name, line], k) => (
          <li key={name} className={`flex gap-3 items-start rounded-xl border-[1.5px] p-3 ${k === 0 ? "border-black bg-[#FFBD4A]" : "border-black/30 bg-white"}`}>
            <span className="font-heading text-xl leading-none w-7 shrink-0">{String(k + 1).padStart(2, "0")}</span>
            <span className="text-sm leading-snug"><strong>{name}:</strong> {line}</span>
          </li>
        ))}
      </ol>
    </Section>

    <Section title="Have these ready — 3 simple things to get you started" testid="welcome-ready">
      <ul className="space-y-2 text-black/80 leading-relaxed">
        {[
          <><strong>Your banking app, or your last 2–3 months of business bank statements</strong>, to spot your regular costs</>,
          <><strong>Your products and services</strong>, plus any plans to launch something new in the next 12 months</>,
          <><strong>A rough idea of your goals</strong> for the next quarter, and any big decisions you're weighing up (e.g. hiring a VA or renting a studio)</>,
        ].map((t, k) => (
          <li key={k} className="flex gap-3 items-start">
            <CheckSquare size={22} weight="duotone" className="shrink-0 mt-0.5" />
            <span>{t}</span>
          </li>
        ))}
      </ul>
    </Section>

    <Section title="Good to know" testid="welcome-good-to-know">
      <ul className="list-disc pl-5 space-y-2 text-black/80 leading-relaxed">
        <li><strong>Give yourself time.</strong> Most people take a few hours, spread over two or three sittings. It's worth it: this is the foundation for everything.</li>
        <li><strong>Your progress saves automatically</strong>, so you can stop at any point and pick up again on any device.</li>
        <li><strong>Everything can be changed later.</strong> Rough numbers are absolutely fine.</li>
        <li><strong>All amounts are rounded to the nearest £1.</strong></li>
        <li><strong>Your answers are only used to build your forecast</strong> and to tailor the coaching from your AI coach.</li>
      </ul>
    </Section>

    <p className="text-lg font-semibold">Ready? Let's do this and see what you can achieve over the next 12 months.</p>
  </div>
);

export default WelcomeStep;
